<template>
  <div class="card">

    <div class="top-row">

      <p @click="goToSurveyPage" class="emoji-icon">{{ survey.emoji_icon }}</p>

      <!-- '...' button that expands to deleting a survey -->
      <a-dropdown :trigger="['click']" v-if="is_survey_creator || is_admin">
        <a-menu slot="overlay">
          <a-menu-item @click="is_edit_survey_modal_visible = true" v-if="!is_demo_survey && survey.is_published !== false">Edit Survey</a-menu-item>
          <a-sub-menu title="Export Data" v-if="survey.is_published !== false">
            <a-menu-item @click="exportCSV()">Answers CSV</a-menu-item>
            <a-menu-item @click="exportJSON()">Answers JSON</a-menu-item>
            <a-menu-item @click="exportMedia()" v-if="is_media_answers_in_survey">Media Files</a-menu-item>
          </a-sub-menu>
          <a-menu-item @click="duplicateSurvey()">Duplicate Survey</a-menu-item>
          <a-menu-item @click="deleteSurvey()">Delete Survey</a-menu-item>
        </a-menu>
        <button class="dotdotdot-btn"></button>
      </a-dropdown>

    </div>

    <!-- survey TITLE (LINKS TO SURVEY) -->
    <h2 class="title" @click="goToSurveyPage">{{ survey.title }}</h2>

    <!-- survey INFO -->
    <p class="info" @click="goToSurveyPage">{{ question_count }} Questions | Created {{ time }} {{ creator_name ? `by ${creator_name}` : '' }}</p>

    <div class="response-count" @click="goToSurveyPage">
      <template v-if="survey.is_published !== false">
        <p><span>{{ started_count }}</span> Started</p>
        <div class="arrow"></div>
        <p><span>{{ completed_count }}</span> Completed</p>
      </template>
      <p v-else><span>Saved as Draft</span></p>
    </div>


    <!-- Survey Card FOOTER -->
    <div class="footer" v-if="shortcode">

      <!-- Published Surveys -->
      <template v-if="survey.is_published">

        <div class="pause-toggler">
          <p :class="{ paused: survey.is_paused }">{{ survey.is_paused ? 'Paused' : 'Live' }}<span v-if="!survey.is_paused">🟢</span></p>
          <a-switch :checked="!survey.is_paused" @change="toggleSurveyPaused" />
        </div>

        <div v-if="!is_demo_survey">
          <a-button v-if="!is_mobile" @click="goToShare"       class="footer-btn share">Share 📣</a-button>
          <a-button v-else            @click="openMobileShare" class="footer-btn copy" >Share 🔗</a-button>
        </div>

      </template>

      <!-- Unpublished Surveys -->
      <template v-else>

        <div>
          <a-button @click="openPreviewModal" class="footer-btn preview">Preview 👁️</a-button>
        </div>
        <div>
          <a-button @click="goToEditSurvey" class="footer-btn edit">Edit ✏️</a-button>
          <a-tooltip :mouseEnterDelay="0.4">
            <template slot="title">Make survey available to everyone and clear all test answers.</template>
            <a-button @click="publishSurvey" class="footer-btn publish">Publish 🚀</a-button>
          </a-tooltip>
        </div>

      </template>

    </div>


    <!-- Preview Code Modal -->
    <sweet-modal overlay-theme="dark" ref="preview_modal" class="preview-modal" width="500" hide-close-button>

      <h4>Preview via WhatsApp</h4>

      <div class="phone-input-container" v-if="paid_push_credits_remaining >= 1">
        <div>
          <vue-tel-input
          v-model="whatsapp_number"
          validCharactersOnly
          dynamicPlaceholder
          mode="international"
          :inputOptions="{ showDialCode: true }"
          @input="validatePhoneNumber"
          class="phone-input"
          :class="{ valid: is_valid_phone_number }"
          ></vue-tel-input>
          <a-button @click="sendSurveyToWhatsapp" class="send-whatsapp-btn">Send</a-button>
        </div>
        <p v-if="show_phone_error">Please enter a valid phone number</p>
      </div>

      <div v-else class="phone-input-container">
        <p>Looks like you're wallet balance is empty. Would you like to <span class="demo-btn" @click="setupDemo">book a demo</span> to discuss getting more? Until then, feel free to use the QR code or link to access your survey.</p>
      </div>

      <p>Or</p>

      <div class="preview-link-container">
        <h4>Visit Survey Link 🔗</h4>
        <div>
          <div>
            <p class="platform-name whatsapp">WhatsApp</p>
            <qrcode :value="qr_deeplink" :options="{ width: 150 }" tag="img"></qrcode>
            <p class="platform-link">{{ https_shortcode }}</p>
          </div>
          <div>
            <p class="platform-name messenger">Messenger</p>
            <qrcode :value="messenger_link" :options="{ width: 150 }" tag="img"></qrcode>
            <p class="platform-link">{{ messenger_link }}</p>
          </div>
        </div>
      </div>


    </sweet-modal>


    <!-- Edit Survey Modal -->
    <a-modal
    v-model="is_edit_survey_modal_visible"
    width="600px"
    :centered="true"
    :closable="false"
    :footer="null"
    :mask="true"
    :bodyStyle="{ padding: 0 }">
      <EditSurveyModal :original_survey="this.survey" :survey_index="this.survey_index" v-on:hideModal="hideEditSurveyModal"/>
    </a-modal>

  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { db_helper } from '../store/db_helper.js';
import VueQrcode from '@chenfengyuan/vue-qrcode';
import { isMobile, isMobileOnly } from 'mobile-device-detect';

import EditSurveyModal from '@/components/EditSurveyModal.vue'

const { parse } = require('json2csv');

export default {
  name: 'surveyCard',
  props: { survey: Object },
  data: function () {
    return {
      is_edit_survey_modal_visible: false,
      whatsapp_number: "",
      is_valid_phone_number: false,
      show_phone_error: false
    }
  },
  components: {
    EditSurveyModal,
    'qrcode': VueQrcode
  },
  computed: {
    survey_index() {
      return this.survey.index;
    },
    shortcode() {
      if (this.survey.shortcode) {
        return `voic.co/${this.survey.shortcode}`
      } else {
        return null;
      };
    },
    https_shortcode() {
      if (this.survey.shortcode) {
        return `https://voic.co/${this.survey.shortcode}`
      } else {
        return null;
      };
    },
    messenger_link() {
      if (this.survey && this.survey.shortcode) {
        return `m.me/ovoic?ref=${this.survey.shortcode}`
      } else {
        return null;
      };
    },
    qr_deeplink() {
      if (this.survey.shortcode) {
        let bot_number = '13513000071';
        if (this.survey.gupshup_phone_number) {
          bot_number = this.survey.gupshup_phone_number;
        };
        if (this.survey.language === 'en' || !this.survey.language) {
          let deeplink = `https://api.whatsapp.com/send?phone=${bot_number}&text=`;
          // Add optional starting message
          if (this.survey.starting_message && this.survey.starting_message.link) {
            deeplink += `${encodeURI(this.survey.starting_message.link)}%0a%0a`;
          };
          deeplink += `%28Send%20this%20message%29%20I%20would%20like%20to%20take%20survey%20code%3a%20${this.survey.shortcode}&source=&data=&app_absent=`;
          return deeplink;
        } else {
          return `https://voic.co/${this.survey.shortcode}`;
        };
      } else {
        return null;
      };
    },

    started_count() {
      // TEMP: Fix for not having started_users on all surveys
      let started_count = _.values(this.survey.started_users).length;
      if (started_count > this.completed_count) {
        return started_count;
      } else {
        return this.completed_count;
      };
    },
    completed_count() {
      return _.values(this.survey.completed_users).length;
    },
    question_count() {
      return _.values(this.survey.questions).length;
    },
    time() {
      return moment.unix(this.survey.time_created).format("MMM Do");
    },
    creator_name() {
      if (this.survey.creator && this.survey.creator.name) {
        let display_name = '';
        let names = this.survey.creator.name.split(' ');
        display_name = names[0];
        if (names.length > 1) {
          display_name  = `${display_name} ${(names[names.length - 1]).charAt(0)}.`;
        };
        return display_name;
      } else {
        return '';
      };
    },
    is_survey_creator() {
      return (this.survey.creator && this.survey.creator.id === this.$store.state.user.uid);
    },
    is_admin() {
      return this.$store.state.user.role === 'admin';
    },
    paid_push_credits_remaining() {
      if (this.$store.state.billing) {
        return Math.floor(this.$store.state.billing.wallet_balance / 0.1);
      } else {
        return 0;
      };
    },
    is_demo_survey() {
      return this.survey.id === "cNUZcoeTjtX1h9vUEama";
    },
    is_share_navigator() {
      return navigator.share;
    },
    is_mobile() {
      return isMobileOnly;
    },
    is_media_answers_in_survey() {
      let is_media = false;
      for (const question_index in this.survey.questions) {
        let question = this.survey.questions[question_index];
        if (['audio', 'video', 'image'].includes(question.answer_type)) {
          is_media = true;
        };
      };
      return is_media;
    },
  },
  methods: {

    exportCSV: async function() {
      let [formatted_answers, keys] = await db_helper.getFormattedSurveyCSV(this.survey);
      const fields = keys;
      const opts = { fields };
      const csv = parse(formatted_answers, opts);
      var dataStr = "data:text/csv;charset=UTF-8," + encodeURIComponent(csv);
      var downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", "survey_data.csv");
      document.body.appendChild(downloadAnchorNode); // required for firefox
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
      analytics.track('Export Data', { type: 'CSV' });
    },

    exportJSON: async function() {
      let formatted_answers = await db_helper.getFormattedSurveyJSON(this.survey);
      var dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(formatted_answers));
      var downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", "survey_data.json");
      document.body.appendChild(downloadAnchorNode); // required for firefox
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
      analytics.track('Export Data', { type: 'JSON' });
    },

    exportMedia: async function() {
      const hide = this.$message.loading('Zipping Media Files', 0);
      let content = await db_helper.exportMedia(this.survey);
      if (content) { // No media to download
        this.$saveAs(content, `${this.survey.title}-media.zip`);
        setTimeout(hide, 5000);
      } else {
        hide();
        this.$message.warning('No media to download (photos, video & audio)', 5);
      };
    },

    openMobileShare: function() {
      analytics.track('Survey Card Link Share Clicked', { type: 'card' });
      navigator.share({
        text: `Click this link to begin your survey on Whatsapp`,
        url: this.qr_deeplink
      });
    },

    openPreviewModal() {
      if (!this.is_mobile) {
        this.$refs.preview_modal.open();
      } else {
        window.open(`https://wa.me/13513000071?text=(Send this message) I would like to take survey code: ${this.survey.shortcode}`);
      };
    },

    sendSurveyToWhatsapp() {
      if (!this.is_valid_phone_number) {
        this.show_phone_error = true;
      } else {
        let wa_phone = this.whatsapp_number;
        let formatted_number = wa_phone.replace(/\D/g, "");
        this.whatsapp_number = '';
        this.show_phone_error = false;
        this.is_valid_phone_number = false;
        let account_name = this.$store.state.account.name || '';
        analytics.track('Whatsapp Push Sent', { account_name, shortcode: this.survey.shortcode, survey_id: this.survey.id });
        let pushes = [{ phone_number: formatted_number }];
        this.$store.dispatch('sendSurveyToWhatsapp', { pushes, survey_index: this.survey_index });
        this.$message.success(`Sending survey to ${wa_phone}..`, 4);
        setTimeout(() => {
          this.$refs.preview_modal.close();
        }, 200);
      }

    },

    validatePhoneNumber(number, data){
      this.is_valid_phone_number = data.valid
    },

    goToSurveyPage() {
      analytics.track('Survey Card Clicked', { shortcode: this.survey.shortcode });
      this.$router.push({ name: 'questions', params: { survey_index: this.survey_index } });
    },

    goToEditSurvey() {
      // Format newlines..
      let unpublished_survey = JSON.parse(JSON.stringify(this.survey));
      _.forEach(unpublished_survey.questions, (question, question_index) => {
        unpublished_survey.questions[question_index].text = unpublished_survey.questions[question_index].text.split('\\n').join('↵');
      });
      this.$store.commit('setDraftSurvey', unpublished_survey);
      this.$router.push({ name: 'newSurvey', params: { survey_index: this.survey_index, is_editing: true }});
    },

    goToShare() {
      this.$router.push({ name: 'share', params: { survey_index: this.survey_index }});
    },

    hideEditSurveyModal() {
      this.is_edit_survey_modal_visible = false;
    },

    publishSurvey() {
      this.$store.dispatch('publishSurvey', this.survey_index);
      this.$store.dispatch('resetSurveyAnswers', this.survey_index);
      this.$message.success(`Survey successfully published!`, 4);
    },

    deleteSurvey() {
      var result = confirm("Are you sure you want to delete this survey?");
      if (result) {
        this.$store.dispatch('deleteSurvey', this.survey.id);
        this.$message.success(`Survey successfully removed`, 4);
        analytics.track('Survey Deleted', { shortcode: this.survey.shortcode });
      };
    },

    duplicateSurvey() {
      this.$store.dispatch('duplicateSurvey', this.survey.id);
      this.$message.success(`Duplicating Survey...`, 5);
      analytics.track('Survey Duplicated', { shortcode: this.survey.shortcode });
    },

    toggleSurveyPaused(checked) {
      let is_paused = !checked;
      this.$store.dispatch('toggleSurveyPaused', { survey_index: this.survey_index, is_paused });
      this.$forceUpdate();
    },

    setupDemo() {
      Calendly.initPopupWidget({url: 'https://calendly.com/outside-voice/intro'})
    },

  }
}
</script>

<style scoped lang="scss">

.card{
  width: 450px;
  padding: 40px 40px 40px 40px;
  margin-top: 25px;
  margin-right: 15px;
  margin-left: 15px;
  background-color: white;
  border: 1px solid #ECECEC;
  border-radius: 8px;
  box-shadow: 0 0 8px 0 rgba(0,0,0,.08);
  align-self: flex-start;
  font-family: "Montserrat", sans-serif;
  p, h2, h3, h3, h4, span{
    color: #0D303F;
  }
}

.top-row{
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  height: 100px;
}

.emoji-icon{
  font-size: 65px;
  line-height: 60px;
  padding-top: 10px;
  padding-bottom: 20px;
  width: 310px;
  margin: 0;
  cursor: pointer;
}

.dotdotdot-btn{
  height: 42px;
  width: 42px;
  background-image: url("../assets/icons/dotdotdot-btn.svg");
  background-repeat: no-repeat;
  background-position: 7.5px 8px;
  background-size: 26px 26px;
  opacity: .7;
  border: none;
  transition: all 0.3s ease;
  transform: translate(15px, 10px);
  border-radius: 6px;
  background-color: rgb(250,250,250);
  cursor: pointer;
  &:hover{
    opacity: 1;
    background-color: rgb(245,245,245);
  }
  &:focus{
    outline: none;
  }
}


.title{
  font-size: 26px;
  font-weight: 700;
  opacity: .85;
  transition: all 0.3s ease;
  margin-bottom: 0px;
  padding-bottom: 13px;
  &:hover{
    cursor: pointer;
    opacity: 1;
  }
}

.info{
  font-size: 14px;
  font-weight: 500;
  height: 60px;
  padding-top: 0px;
  margin-bottom: 0px;
  color: rgba(30,30,30,.8);
  cursor: pointer;
}

.response-count{
  vertical-align: bottom;
  position: relative;
  width: 100%;
  height: 25px;
  cursor: pointer;
  p{
    font-size: 15px;
    font-weight: 500;
    margin-bottom: 8px;
    display: inline-block;
    vertical-align: top;
  }
  span{
    font-weight: 700;
  }
  .arrow{
    width: 30px;
    height: 30px;
    background-image: url("../assets/icons/arrow-right.svg");
    background-repeat: no-repeat;
    background-position: 5px 3px;
    background-size: 16px;
  }
  & > div{
    display: inline-block;
    vertical-align: bottom;
  }
}

.footer{
  margin-top: 30px;
  background-color: rgb(250,250,250);
  width: 448px;
  height: 80px;
  padding: 0px 30px 3px 40px;
  margin-bottom: -40px;
  transform: translateX(-40px);
  border-radius: 0 0 6px 6px;
  border-top: 1px solid rgb(230,230,230);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  justify-content: space-between;
  p.link{
    font-size: 17px;
    font-weight: 500;
    margin-bottom: 0px;
    display: inline-block;
  }
  & > div{
    //white-space: nowrap;
  }
  .footer-btn{
    font-size: 14px;
    font-weight: 600;
    color: rgb(30,30,30);
    height: 39px;
    background-color: rgb(255,255,255);
    padding: 0px 13px 1px 13px;
    margin-left: 18px;
    opacity: .85;
    border-radius: 6px;
    border: 1px solid rgb(210,210,210);
    box-shadow: 0 0 3px 0 rgba(0,0,0,.05);
    transition: all 0.3s ease;
    cursor: pointer;
    &:hover{
      opacity: 1;
      background-color: rgb(250,250,250);
    }
    &:first-of-type:not(.copy){
      margin-left: 0px;
    }
    &:focus{
      outline: none;
    }
    &.preview{
      padding-right: 15px;
    }
    &.publish,
    &.edit{
      padding-right: 10px;
    }
    &.publish,
    &.share{
      color: white;
      background: #2c3052; /* Old browsers */
      opacity: .95;
      background: -moz-linear-gradient(top, #2c3052 0%, #1e2032 100%); /* FF3.6-15 */
      background: -webkit-linear-gradient(top, #2c3052 0%,#1e2032 100%); /* Chrome10-25,Safari5.1-6 */
      background: linear-gradient(to bottom, #2c3052 0%,#1e2032 100%); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
      border-color: black;
    }
    &.share{
      margin-left: 84px;
    }
  }
  .pause-toggler{
    display: flex;
    flex-direction: row;
    p{
      font-size: 14px;
      font-weight: 600;
      margin: 0;
      margin-right: 10px;
      width: 55px;
      &.paused{
        opacity: .5;
      }
      span{
        font-size: 7px;
        margin-left: 6px;
        vertical-align: middle;
      }
    }
    .ant-switch-checked{
      background: linear-gradient(135deg, #36dca7 1%,#19c6af 100%);
      border: 0.5px solid #20A379;
    }
    & > *{
      display: inline-block;
    }
  }
}

div.sweet-modal.is-visible{
  border-radius: 8px !important;
  padding-top: 5px;
  padding-bottom: 5px;
}


.preview-modal{
  h4{
    font-size: 16px;
    font-weight: 600;
  }
  .phone-input-container{
    width: 85%;
    height: 80px;
    margin: 0 auto;
    margin-top: 35px;
    margin-bottom: 20px;
    & > div{
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }
    .phone-input{
      flex-grow: 1;
      margin-right: 15px;
      border-color: rgb(220,220,220);
      font-size: 15px;
      padding: 3px;
      &.valid{
        border-color: #1DB248;
      }
    }
    button{
      width: 94px;
      height: 40px;
      font-weight: 600;
      &.send-whatsapp-btn{
        background-image: url("../assets/icons/whatsapp-btn-icon.svg");
        background-repeat: no-repeat;
        background-position: 59px 9px;
        background-size: 20px;
        padding-right: 52px;
      }
    }
    p{
      font-size: 13px;
      margin-top: 15px;
      margin-bottom: 0px;
      span{
        font-weight: 600;
        &.demo-btn{
          color: #1BD285;
          cursor: pointer;
        }
      }
    }
    .push-usage{
      margin-top: 19px;
      font-size: 16px;
      span{
        font-weight: 600;
      }
    }
  }
  .preview-link-container{
    margin-bottom: -10px;
    & > div{
      width: 85%;
      margin: 0 auto;
      margin-top: 25px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      .platform-name{
        font-size: 15px;
        font-weight: 600;
        background-repeat: no-repeat;
        transform: translateX(-12px);
        &.whatsapp{
          background-image: url("../assets/whatsapp-logo.svg");
          background-size: 18px;
          background-position: 122px 3px;
        }
        &.messenger{
          background-image: url("../assets/icons/messenger-btn-icon.svg");
          background-size: 25px;
          background-position: 128px -1px;
        }
      }
      .platform-link{
        font-size: 15px;
        font-weight: 500;
      }
    }
  }
}

input.vti__input{
  font-size: 15px !important;
  font-weight: 500 !important;
}

.sweet-modal.is-visible .sweet-buttons, .sweet-modal.is-visible .sweet-content{
  padding-top: 25px !important;
  padding-bottom: 25px !important;
}

@media only screen
and (min-device-width : 320px)
and (max-device-width : 480px) {

.card{
  width: 100%;
  margin-left: 0px;
  margin-right: 0px;
}

.footer{
  width: 100%;
  background: none;
  border-top: none;
  padding: 0px 0px 3px 0px;
  transform: translateX(0px);
  .footer-btn{
    font-size: 12px;
    height: 32px;
    padding: 0px 13px 1px 13px;
    margin-left: 18px;
    &.preview{
      margin-right: 20px;
    }
    &.publish,
    &.edit{
      padding-right: 8px;
    }
    &.share{
      float: right;
      background-position: 58px 5px;
      margin-left: 68px !important;
    }
  }
}

}


/* Ipad Portrait */
@media only screen and (min-device-width : 768px) and (max-device-width : 1024px) and (orientation : portrait) {
  .card{
    width: 45%;
  }
  .footer{
    width: 100%;
    background: none;
    border-top: none;
  }
}

</style>
